import { IconAlertCircle } from '@iwoca/icons';
import { fetchPostXeroLogin } from '@iwoca/lapi-client/edge';
import { AlertBox } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useParams } from 'react-router';

import externalLinkSVG from '../../../../assets/externalLink.svg';
import { buildQueryString } from '../../../../Buyer/utils/queryParams';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { createToast } from '../../../../store/IwToast';
import { useIwocapaySellerXeroAccount } from '../../../Xero/Xero.helpers';
import tickSVG from '../../assets/tick.svg';
import { useGetIntegrationStatus } from '../../hooks/useGetIntegrationStatus.hook';
import { Xero } from '../../Integrations';
import { ConnectButton } from '../ConnectButton/ConnectButton';
import { IntegrationsTitle } from '../IntegrationsTitle/IntegrationsTitle';

export function XeroBody() {
  const { stateKey } = useStateKey();
  const { integrationParam } = useParams();
  const { integrationStatus, loadingIwocapaySellerConnection } =
    useGetIntegrationStatus({
      integrationId: Xero.id,
    });
  const isConnected = integrationStatus === 'CONNECTED';
  const { data: iwocapayXeroAccount } = useIwocapaySellerXeroAccount({
    enabled: isConnected,
  });

  const buttonText = isConnected ? 'Configure plugin' : 'Connect';
  const buttonLink = isConnected ? '/pay/xero/account' : '/pay/xero/connect';

  const redirectUserToXero = async () => {
    if (!stateKey) {
      createToast('Unexpected error.', {
        variant: 'failure',
      });
      return;
    }

    const queryString = buildQueryString();
    try {
      const response = await fetchPostXeroLogin({
        reason: 'iwocapay_seller',
        stateKey: stateKey!,
        body: {
          data: {
            redirect_to: `/pay/xero/authorisation/${queryString}`,
          },
        },
      });
      window.location.href = response.data.authorization_url;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      createToast('We weren’t able to redirect you, please try again.', {
        variant: 'failure',
      });
    }
  };

  const redirectToRelevantPath = () => {
    if (isConnected) {
      // For Seller fee reporting we needed to extend the oAuth scopes for Xero
      // so we assume if they haven't linked a Fee account they likely haven't re-authed us
      const initial_feeAccount_id =
        // @ts-expect-error fee_account needs to be added to endpoint response
        iwocapayXeroAccount?.data?.seller_account?.fee_account?.fee_account_id;

      if (!initial_feeAccount_id) {
        void redirectUserToXero();
        return;
      }
    }
    const queryString = buildQueryString();
    window.location.href = `${buttonLink}${queryString}`;
  };

  if (integrationParam !== Xero.id) {
    return null;
  }
  return (
    <div className="text-left">
      <div className="flex flex-wrap items-center justify-between max-800:flex-col max-800:items-start max-800:pt-[2.25rem]">
        <IntegrationsTitle integration={Xero} />
        <div
          className={clsx('max-800:w-full max-800:pt-l', {
            'flex flex-col items-end max-800:flex-row max-800:justify-between':
              isConnected,
          })}
        >
          {isConnected && !loadingIwocapaySellerConnection && (
            <div className="pb-s pt-l text-end text-l font-bold text-success-drk max-800:mb-auto max-800:mt-auto max-800:p-0">
              <img src={tickSVG} alt="Tick Icon" className="pr-[0.7rem]" />
              Connected
            </div>
          )}
          {!loadingIwocapaySellerConnection && (
            <ConnectButton
              isConnected={isConnected}
              onClick={redirectToRelevantPath}
            >
              {buttonText}
            </ConnectButton>
          )}
        </div>
      </div>
      <div className="border-b border-structure-80 pb-[3rem] text-m font-reg">
        <div>
          <p>
            Online accounting software for your business connects you to your
            bank, accountant, bookkeeper, and other business apps.
          </p>
          <h3 className="mb-0 text-m font-bold">
            When you connect iwocaPay to Xero you can:
          </h3>
          <ul className="mt-s">
            <li>
              include a ‘Pay with iwocaPay’ button on Xero invoices, without
              creating pay links
            </li>
            <li>
              automatically record invoice payments in Xero when your customers
              use iwocaPay.
            </li>
          </ul>
          <h3 className="mb-0 text-m font-bold">What you'll need in Xero:</h3>
          <ul className="mt-s">
            <li>an account for your iwocaPay payments to be logged in</li>
            <li>
              an invoice branding theme you’d like to add an iwocaPay button to.
            </li>
          </ul>
          <div>
            <AlertBox variant="connect" className="mt-2xl flex gap-s">
              <IconAlertCircle className="mt-xs min-w-[19px]" />
              <div>
                <strong>
                  You’ll need to set up Two-Factor Authentication to keep your
                  account secure -
                </strong>{' '}
                You’ll need another device (like your phone) to set it up. We’ll
                send a security code to that device you’ll need to enter every
                time you access your iwocaPay account, so make sure it’s one you
                have access to.
              </div>
            </AlertBox>
            <a
              href={
                'https://ecommerce-api.iwoca.com/guides/plugin-solution/xero/'
              }
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center pt-[1.25rem] text-m font-bold text-primary-40 no-underline"
            >
              Xero FAQs
              <img
                src={externalLinkSVG}
                alt="External Link Icon"
                className="pl-s"
              />
            </a>
          </div>
        </div>
      </div>
      <div className="inline-flex pt-[3rem] max-800:block max-800:w-full">
        {!loadingIwocapaySellerConnection && (
          <ConnectButton
            isConnected={isConnected}
            onClick={redirectToRelevantPath}
          >
            {buttonText}
          </ConnectButton>
        )}
      </div>
    </div>
  );
}
